import React, { useState } from 'react';
import MaisAcoes, { ItemDeMenu } from './shared/MaisAcoes';
import { NaturezaDoItem, OrigemDoValor, valorTravado } from '../utils/naturezaDoItem';
import { fmtPercentual } from '../utils/rateioDoGrupo';
import { TONE_BORDER, TONE_TEXT } from '../utils/statusColors';
import { campo } from '../design';

/**
 * A linha de um item MRR no EDITOR da assinatura. Prévia aprovada pelo Heitor em
 * 05/10/2026: https://claude.ai/artifact/KzKn2krSKxjD6DkbRjtrxG
 *
 * O que muda de natureza para natureza (utils/naturezaDoItem):
 *  - PRÓPRIO: produto, quantidade e unitário editáveis;
 *  - RATEADO e DESCONTO DO GRUPO: o VALOR é derivado do rateio e não se digita
 *    (produto, quantidade, unitário e desc % travados, com cadeado). A coluna
 *    "De onde vem" diz o percentual que GEROU o valor gravado, não o de hoje.
 *  - Todo desconto (do grupo ou próprio) escolhe o ALVO na própria grade, na
 *    coluna "Abate em". Antes era uma linha solta embaixo do item.
 *
 * As regras de atualização do formulário continuam no App (callbacks): a linha
 * só decide o que se mostra e o que se digita.
 */
export interface AcoesDaLinha {
  onDescricao: () => void;
  onVigencia: () => void;
  onEmpenho: () => void;
  onDescontoPct: () => void;
  onRemover: () => void;
}

interface Props {
  item: any;
  natureza: NaturezaDoItem;
  origem: OrigemDoValor | null;
  /** Produtos ativos, já ordenados, para o seletor. */
  produtos: any[];
  /** Todos os itens da assinatura (alvos possíveis do desconto e "recebe o desconto"). */
  itens: any[];
  idx: number;
  /** Locais do cliente; vazio = sem coluna Filial. */
  locais: any[];
  fmt: (v: number) => string;
  descricaoAberta: boolean;
  temVigencia: boolean;
  temEmpenho: boolean;
  onProduto: (productId: string) => void;
  onFilial: (locationId: string) => void;
  onQuantidade: (valor: string) => void;
  onUnitario: (valor: string) => void;
  onAlvo: (productId: string) => void;
  acoes: AcoesDaLinha;
}

const campoDaLinha = `w-full px-2 py-1 rounded-md text-sm ${campo}`;

const Cadeado = () => (
  <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' aria-hidden='true' className='shrink-0 text-slate-500'>
    <rect x='4' y='11' width='16' height='10' rx='2' />
    <path d='M8 11V7a4 4 0 0 1 8 0v4' />
  </svg>
);

/** Os itens em que um desconto pode abater: positivos, com produto, do mesmo tipo fiscal. */
export function alvosDoDesconto(item: any, itens: any[], produtos: any[], idx: number): any[] {
  const prodDoDesconto = produtos.find((p: any) => Number(p.id) === Number(item.productId));
  const tipoFiscal = prodDoDesconto?.fiscalType;
  return itens.filter((oi: any, i: number) => {
    if (i === idx || Number(oi.totalValue) <= 0 || !oi.productId) return false;
    if (tipoFiscal && tipoFiscal !== 'isento') {
      const p = produtos.find((x: any) => Number(x.id) === Number(oi.productId));
      if (p?.fiscalType && p.fiscalType !== tipoFiscal) return false;
    }
    return true;
  });
}

export default function LinhaDoItemMrr({
  item, natureza, origem, produtos, itens, idx, locais, fmt,
  descricaoAberta, temVigencia, temEmpenho,
  onProduto, onFilial, onQuantidade, onUnitario, onAlvo, acoes,
}: Props) {
  const [trocandoProduto, setTrocandoProduto] = useState(false);
  const travado = valorTravado(natureza);
  const prod = produtos.find((p: any) => Number(p.id) === Number(item.productId));
  const nome = item.name || prod?.name || 'Produto';
  const codigo = item.productCode || prod?.code || '';
  const ehDesconto = Number(item.totalValue) < 0;
  const desconto = Number(item.discount) || 0;
  const mostraSeletor = !item.productId || trocandoProduto;
  // Item próprio em que algum desconto desta assinatura abate.
  const recebe = !ehDesconto && item.productId
    ? itens.filter((oi: any) => Number(oi.totalValue) < 0 && Number(oi.discountTargetProductId) === Number(item.productId))
    : [];

  const menu: ItemDeMenu[] = [
    { label: descricaoAberta ? 'Esconder descrição' : 'Descrição', onClick: acoes.onDescricao },
    ...(item.productId ? [
      { label: temVigencia ? 'Vigência (definida)' : 'Vigência', onClick: acoes.onVigencia, title: 'Item que começa ou termina no meio do período faturado' },
      { label: temEmpenho ? `Empenho (${String(item.empenhoNumber).trim()})` : 'Empenho', onClick: acoes.onEmpenho, title: 'Empenho estável só deste item (ex.: locação). O que muda todo mês vai no Nº do Empenho da fatura.' },
    ] : []),
    ...(!travado ? [{ label: desconto ? `Desconto % (${desconto}%)` : 'Desconto %', onClick: acoes.onDescontoPct }] : []),
    ...(natureza === 'proprio' && item.productId ? [{ label: 'Trocar produto', onClick: () => setTrocandoProduto(true) }] : []),
    { label: 'Remover', danger: true, onClick: acoes.onRemover },
  ];

  return (
    <tr className='text-slate-300 hover:bg-slate-800/50' data-natureza={natureza}>
      <td className='px-2 py-1.5'>
        {mostraSeletor ? (
          <select
            value={String(item.productId || '')}
            onChange={(e) => { onProduto(e.target.value); setTrocandoProduto(false); }}
            onBlur={() => setTrocandoProduto(false)}
            autoFocus={trocandoProduto}
            aria-label='Produto'
            className={`${campoDaLinha} min-w-0 truncate`}
            title={prod ? `${prod.code} - ${prod.name}` : ''}
          >
            <option value=''>Selecione...</option>
            {produtos.map((p: any) => <option key={p.id} value={String(p.id)}>{p.code} - {p.name} ({p.type})</option>)}
          </select>
        ) : (
          <div className='min-w-0 pl-1'>
            <div className='flex items-center gap-1.5 min-w-0'>
              <span className='truncate text-sm text-white' title={nome}>{nome}</span>
              {desconto > 0 && <span className={`shrink-0 text-xs ${TONE_TEXT.danger}`} title='Desconto % deste item'>−{desconto}%</span>}
            </div>
            {codigo && <div className='font-mono text-[11px] text-slate-500 truncate'>{codigo}</div>}
          </div>
        )}
      </td>
      {locais.length > 0 && (
        <td className='px-2 py-1.5'>
          <select value={item.locationId || ''} onChange={(e) => onFilial(e.target.value)} aria-label='Filial' className={`w-full px-1.5 py-1 rounded-md text-xs ${campo}`}>
            <option value=''>Matriz</option>
            {locais.map((loc: any) => <option key={loc.id} value={loc.id}>{loc.code}</option>)}
          </select>
        </td>
      )}
      <td className='px-2 py-1.5'>
        {travado ? (
          <div className='text-center text-sm tabular-nums text-slate-300'>{item.quantity || 1}</div>
        ) : (
          <input type='number' min='1' value={item.quantity || 1} onChange={(e) => onQuantidade(e.target.value)} aria-label='Quantidade' className={`${campoDaLinha} text-center`} />
        )}
      </td>
      <td className='px-2 py-1.5'>
        {travado ? (
          <div className='flex items-center justify-end gap-1.5 text-sm tabular-nums text-slate-300' title='Vem do rateio do grupo: não se digita. Muda-se no rateio.'>
            <Cadeado />
            <span aria-label='Unitário derivado do rateio'>{fmt(Number(item.unitValue) || 0)}</span>
          </div>
        ) : (
          <input type='number' step='0.01' value={item.unitValue || 0} onChange={(e) => onUnitario(e.target.value)} aria-label='Unitário' className={`${campoDaLinha} text-right`} />
        )}
      </td>
      <td className='px-3 py-1.5 text-right'>
        <span className={`font-semibold whitespace-nowrap tabular-nums ${ehDesconto ? 'text-red-400' : 'text-green-400'}`}>R$ {fmt(item.totalValue || 0)}</span>
      </td>
      <td className='px-2 py-1.5 text-xs'>
        {ehDesconto && (
          <label className='flex items-center gap-1.5'>
            <span className={`whitespace-nowrap ${TONE_TEXT.danger}`}>Abate em</span>
            <select value={item.discountTargetProductId || ''} onChange={(e) => onAlvo(e.target.value)} className={`min-w-0 flex-1 px-1.5 py-1 bg-slate-700 border ${TONE_BORDER.danger} text-white rounded text-xs focus:border-orange-500`}>
              <option value=''>Auto (menor item)</option>
              {alvosDoDesconto(item, itens, produtos, idx).map((oi: any) => {
                const op = produtos.find((p: any) => Number(p.id) === Number(oi.productId));
                return <option key={oi.productId} value={oi.productId}>{op?.code || ''} - {op?.name || oi.name} (R$ {fmt(oi.totalValue)})</option>;
              })}
            </select>
          </label>
        )}
        {origem && (
          <div className='mt-0.5 text-slate-400' data-testid={`origem-${idx}`}>
            <span className={origem.daDivisaoVigente ? '' : TONE_TEXT.warning}>{fmtPercentual(origem.percentual)}</span>
            {` de R$ ${fmt(origem.valorCheio)}`}
            <span className='block text-slate-500'>{origem.daDivisaoVigente ? 'divisão vigente' : 'divisão anterior à vigente'}</span>
          </div>
        )}
        {recebe.length > 0 && (
          <span className='text-slate-500'>recebe {recebe.map((d: any) => d.name || 'desconto').join(', ')}</span>
        )}
      </td>
      <td className='px-2 py-1.5'>
        <div className='flex justify-end'>
          <MaisAcoes itens={menu} title='Ações do item' />
        </div>
      </td>
    </tr>
  );
}
